import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess,
  ExpandMore,
  Group as GroupIcon,
  Lock as LockIcon,
  Receipt,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import { IProcessManager } from '../../Interfaces/IProcessManager';
import { IProcessManagerAutomation } from '../../Interfaces/IProcessManagerAutomation';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import { IHbciAccount, IHbciTanChallenge } from '../../Interfaces/IHbciAccount';
import { HbciAccountVisibilityDialog } from '../HbciAccountVisibilityDialog';
import { PMAutomationsTable } from '../../ProcessManager/Automations/PMAutomationsTable';
import { useRefreshHbciAccount } from '../../hooks/reactQuerys/HbciAccountQueries';
import { useAlert } from '../../context/AlertContext';
import { useConfirmation } from '../../context/ConfirmationContext';
import { HbciTransactionsDialog } from '../HbciTransactionsDialog';
import { HbciTanChallengeDialog } from '../HbciTanChallengeDialog';
import {
  getTrackerAccountState,
  getTrackerStatus,
  getTrackersUsingAccount,
  readTrackerAccountId,
} from './hbciTrackerStatus';

interface IProps {
  pm: IProcessManager;
  account?: IHbciAccount;
  /** Gesamtes PM-Array, damit beim Entfernen geprueft werden kann, ob das
   *  zugehoerige HBCI-Konto noch von anderen Trackings benutzt wird. */
  processManagerArray: IProcessManager[];
  // Parent reicht seinen eigenen React-setState weiter — explizit getypt
  // statt als ``Function``, damit kein eslint-Bypass noetig ist. Akzeptiert
  // sowohl das neue Array direkt als auch eine Updater-Funktion (klassische
  // React-setState-Signatur), und bleibt damit kompatibel zu den
  // Parents im LegalTransaction-Baum.
  setProcessManagerArray: (
    next: IProcessManager[] | ((prev: IProcessManager[]) => IProcessManager[])
  ) => void;
  legalClients: ILegalClient[];
  legalPhases: ILegalPhaseFullObject[];
  legalReqStates: ILegalRequirementState[];
  /** Rollen des Vorgangs — für die Sichtbarkeits-Einschränkung des Kontos. */
  roles?: ILegalRole[];
  idLegalTransaction: number | null;
  /** Konten des Vorgangs werden noch geladen. */
  isLoadingAccounts?: boolean;
  /** Konten-Abruf ist fehlgeschlagen (z. B. 403 bei inaktiver Erweiterung) —
   *  dann darf die Zeile NICHT behaupten, das Konto sei gelöscht. */
  hasAccountLoadError?: boolean;
}

const formatBalance = (val: number | null | undefined): string => {
  if (val === null || val === undefined) return '–';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(val);
};

const parseTargetAmount = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return raw;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Auslöser-Buckets: jede Automation traegt im ``data.Trigger`` einen
 *  dieser Werte. Der PM-Driver fuehrt eine Automation nur aus, wenn das
 *  passende Event eintritt. Beide Buckets koennen gleichzeitig befuellt
 *  sein — fuer Saldo-Aenderung UND Zielsumme einzeln.
 */
const TRIGGER_BALANCE = 'balance-change';
const TRIGGER_TARGET = 'target-reached';

/** Steuerung, wie oft die ``Bei Saldo-Änderung``-Automationen feuern.
 *  Wird im PM-Driver ausgewertet. Eine „ewig laufende" Variante gibt es
 *  bewusst nicht — jede Verfolgung soll ein klares Ende-Kriterium haben:
 *   - ``until-target`` (Default): solange feuern, bis die Zielsumme erreicht ist
 *   - ``once``                  : nur beim ersten Saldo-Wechsel, dann stop
 */
type BalanceChangeMode = 'until-target' | 'once';

const BALANCE_CHANGE_MODE_LABELS: Record<BalanceChangeMode, string> = {
  'until-target': 'Bei jeder Änderung, bis Zielsumme erreicht ist (Standard)',
  once: 'Nur einmalig – bei der ersten Saldo-Änderung',
};

/**
 * Eine Zeile in der „d. Notaranderkonten"-Tabelle der
 * Informationsverfolgung. Bietet:
 *   - Trigger-Toggle (Saldo-Änderung vs. Zielsumme)
 *   - Manueller HBCI-Refresh (Saldo holen)
 *   - Kontobewegungen-Dialog (HKKAZ)
 *   - Aufklappbarer Bereich für Automationen (Email/Anforderungs-Update)
 *   - Entfernen
 */
export const ProcessManagerHbciRow: React.FC<IProps> = ({
  pm,
  account,
  processManagerArray,
  setProcessManagerArray,
  legalClients,
  legalPhases,
  legalReqStates,
  roles = [],
  idLegalTransaction,
  isLoadingAccounts = false,
  hasAccountLoadError = false,
}) => {
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const { refreshHbciAccount } = useRefreshHbciAccount(idLegalTransaction ?? 0);

  const data =
    (pm.data as {
      idHbciAccount?: number | string;
      TargetAmount?: string | number;
      TargetReachedAt?: string;
      // Free-form, weil Bestandsdaten ggf. veraltete Werte wie
      // ``always`` enthalten — wir normalisieren beim Lesen.
      BalanceChangeMode?: string;
      BalanceChangeFiredAt?: string;
    }) || {};
  const idHbci = readTrackerAccountId(pm);
  const target = parseTargetAmount(data.TargetAmount);
  // Default-Mode: ``until-target`` — Verfolgungen sollen ein klares
  // Ende-Kriterium haben. Bestandsdaten mit altem ``always`` oder ohne
  // Feld werden hier auf ``until-target`` gemappt. Wenn keine Zielsumme
  // gesetzt ist und der User trotzdem im Bucket aktiv ist, fallen wir
  // visuell auf ``once`` zurueck (Backend macht dieselbe Fallback-Logik).
  const rawMode = data.BalanceChangeMode;
  const balanceChangeMode: BalanceChangeMode =
    rawMode === 'once' ? 'once' : target === null ? 'once' : 'until-target';
  // Wenn der ``once``-Modus bereits gefeuert hat, ist er „verbraucht".
  const balanceChangeOnceConsumed =
    balanceChangeMode === 'once' && Boolean(data.BalanceChangeFiredAt);
  // Sobald die Zielsumme-Automation einmal getriggert hat, friert das
  // Backend ``data.TargetReachedAt`` ein — danach darf die Zielsumme
  // nicht mehr veraendert werden, sonst wuerde sie potenziell erneut
  // ausloesen koennen.
  const targetLocked = Boolean(data.TargetReachedAt);

  const balance = account?.CurrentBalance ?? null;
  const targetReached =
    target !== null && balance !== null && balance >= target;
  const isNew = pm.idProcessManager < 0;

  // Verknüpfungs-Zustand: unterscheidet "keine ID hinterlegt" von "Konto
  // existiert nicht mehr" und von "Abruf fehlgeschlagen/läuft noch".
  const accountState = getTrackerAccountState({
    idHbciAccount: idHbci,
    account,
    isLoadingAccounts,
    hasAccountLoadError,
  });
  // Ohne benutzbares Konto laufen Saldo-Abruf, Kontobewegungen, Sichtbarkeit
  // und Duplizieren zwangsläufig ins Leere (404) — deshalb gesperrt.
  const accountActionsDisabled =
    accountState === 'unlinked' || accountState === 'missing';
  const status = getTrackerStatus({
    isNew,
    idProcessManagerState: pm.idProcessManagerState,
    targetReached,
    targetLocked,
  });

  const [expanded, setExpanded] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tanChallenge, setTanChallenge] = useState<IHbciTanChallenge | null>(
    null
  );
  const [editTargetOpen, setEditTargetOpen] = useState(false);
  const [editTargetValue, setEditTargetValue] = useState<string>(
    target !== null ? String(target).replace('.', ',') : ''
  );

  const updatePmData = (patch: Record<string, unknown>) => {
    setProcessManagerArray((prev: IProcessManager[]) =>
      prev.map(p =>
        p.idProcessManager === pm.idProcessManager
          ? { ...p, data: { ...(p.data as object), ...patch } }
          : p
      )
    );
  };

  const handleSaveTarget = () => {
    const raw = editTargetValue.trim();
    if (raw === '') {
      updatePmData({ TargetAmount: '' });
      setEditTargetOpen(false);
      return;
    }
    const normalized = raw.replace(/\s/g, '').replace(',', '.');
    const num = parseFloat(normalized);
    if (!Number.isFinite(num) || num < 0) {
      showAlert({
        text: 'Bitte einen gültigen Betrag in EUR eintragen.',
        severity: 'warning',
      });
      return;
    }
    updatePmData({ TargetAmount: normalized });
    setEditTargetOpen(false);
  };

  const handleCopy = () => {
    // Negativ-ID fuer den neuen Eintrag (analog zu LandRegister-copyMe).
    const minId = processManagerArray.length
      ? Math.min(...processManagerArray.map(p => p.idProcessManager))
      : 0;
    const tmpId = (minId <= 0 ? minId : 0) - 1;

    // Kopie ohne TargetReachedAt + ohne Sweep-Stempel — die Kopie soll
    // sich verhalten wie ein frisch angelegter Tracker. Automations
    // werden mitkopiert, ihre tempIDs werden neu vergeben, damit sie
    // beim Save nicht mit den Originalen kollidieren.
    let autoTmpId = Math.min(
      ...(pm.Automations ?? []).map(a => a.idProcessManagerAutomation),
      -1
    );
    const copiedAutomations = (pm.Automations ?? []).map(a => ({
      ...a,
      idProcessManagerAutomation: --autoTmpId,
      idProcessManager: tmpId,
    }));

    // Sicherheits-Check: ohne (existierendes) Konto macht eine Kopie keinen
    // Sinn — der Backend-Driver wuerde sie sofort als „PM-Eintrag ohne
    // idHbciAccount" markieren bzw. sie liefe ins Leere.
    if (idHbci === null || accountState === 'missing') {
      showAlert({
        text:
          idHbci === null
            ? 'Dieser Verfolgung ist kein Notaranderkonto zugeordnet. Sie kann daher nicht dupliziert werden.'
            : 'Das zugeordnete Notaranderkonto ist nicht mehr vorhanden. Die Verfolgung kann daher nicht dupliziert werden.',
        severity: 'warning',
      });
      return;
    }

    // Kopie ohne TargetReachedAt + ohne Sweep-Stempel — die Kopie soll
    // sich verhalten wie ein frisch angelegter Tracker. idHbciAccount +
    // TargetAmount + BalanceChangeMode werden EXPLIZIT uebernommen
    // (statt via Rest-Spread), damit ein irgendwie korruptes ``data``-
    // Object niemals zu einem PM ohne Konto-Verknuepfung fuehren kann.
    const newData: Record<string, unknown> = {
      idHbciAccount: idHbci,
    };
    if (data.TargetAmount !== undefined && data.TargetAmount !== '') {
      newData.TargetAmount = data.TargetAmount;
    }
    if (data.BalanceChangeMode) {
      newData.BalanceChangeMode = data.BalanceChangeMode;
    }

    const newPm = {
      ...pm,
      idProcessManager: tmpId,
      idProcessManagerState: 1,
      Tries: 0,
      LastTry_at: null,
      ExitCodeLastTry: null,
      Created_at: new Date(),
      Updated_at: new Date(),
      Closed_at: null,
      data: newData,
      Automations: copiedAutomations,
    };

    setProcessManagerArray((prev: IProcessManager[]) => [...prev, newPm]);
    showAlert({
      text: 'Notaranderkonto-Verfolgung kopiert.',
      severity: 'success',
    });
  };

  const updateAutomations = (next: IProcessManagerAutomation[]) => {
    setProcessManagerArray((prev: IProcessManager[]) =>
      prev.map(p =>
        p.idProcessManager === pm.idProcessManager
          ? { ...p, Automations: next }
          : p
      )
    );
  };

  const handleRefresh = async () => {
    if (isNew) {
      showAlert({
        text: 'Bitte zuerst speichern, dann kann der Saldo abgerufen werden.',
        severity: 'info',
      });
      return;
    }
    if (idHbci === null || accountState === 'missing') {
      showAlert({
        text: 'Dieser Verfolgung ist kein abrufbares Notaranderkonto zugeordnet.',
        severity: 'warning',
      });
      return;
    }
    setRefreshing(true);
    try {
      const result = (await refreshHbciAccount(idHbci)) as {
        tanRequired?: boolean;
        challenge?: IHbciTanChallenge;
        error?: string;
        hbciError?: string;
        CurrentBalance?: number | null;
      };
      if (result?.tanRequired) {
        setTanChallenge(result.challenge as IHbciTanChallenge);
        return;
      }
      if (result?.error || result?.hbciError) {
        showAlert({
          text: `HBCI-Fehler: ${result.error || result.hbciError}`,
          severity: 'error',
        });
        return;
      }
      showAlert({
        text: `Kontostand aktualisiert: ${formatBalance(result?.CurrentBalance)}`,
        severity: 'success',
      });
    } catch (e) {
      showAlert({
        text:
          e instanceof Error
            ? `HBCI-Abruf fehlgeschlagen: ${e.message}`
            : 'HBCI-Abruf fehlgeschlagen.',
        severity: 'error',
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleRemove = async () => {
    // Andere Verfolgungen auf dasselbe Konto — bewusst ueber ALLE States:
    // eine abgeschlossene Verfolgung (State 3) zeigt genauso auf das Konto
    // wie eine offene und wuerde sonst verwaisen.
    const otherTrackingsUsingAccount = getTrackersUsingAccount(
      processManagerArray,
      pm,
      idHbci
    );
    const willAlsoDeleteAccount =
      idHbci !== null &&
      accountState !== 'missing' &&
      otherTrackingsUsingAccount.length === 0;

    // Nichts wird hier geloescht: der Eintrag verschwindet nur aus dem
    // Vorgangs-Array. Wirksam wird das Entfernen (Verfolgung, danach Konto)
    // erst beim Speichern des Vorgangs im Backend — analog zu Mandanten,
    // Beteiligten und Dateien. Der Dialog benennt genau die Folge; die Aktion
    // selbst steht schon im Titel und in der Schaltflaeche.
    const otherCount = otherTrackingsUsingAccount.length;
    const accountLabel = account?.IBAN ?? `#${idHbci}`;
    const message = isNew ? (
      'Die Verfolgung ist noch nicht gespeichert und wird ohne weitere Auswirkung verworfen.'
    ) : accountState === 'unlinked' ? (
      'Dieser Verfolgung ist kein Notaranderkonto zugeordnet. Das Entfernen wird mit dem Speichern des Vorgangs wirksam.'
    ) : accountState === 'missing' ? (
      'Das zugeordnete Notaranderkonto ist nicht mehr vorhanden. Das Entfernen der Verfolgung wird mit dem Speichern des Vorgangs wirksam.'
    ) : otherCount > 0 ? (
      <>
        Das Notaranderkonto <strong>{accountLabel}</strong> bleibt erhalten, da
        es{' '}
        {otherCount === 1
          ? 'von einer weiteren Verfolgung'
          : `von ${otherCount} weiteren Verfolgungen`}{' '}
        genutzt wird. Das Entfernen dieser Verfolgung wird mit dem Speichern des
        Vorgangs wirksam.
      </>
    ) : (
      <>
        Mit der Verfolgung wird auch das Notaranderkonto{' '}
        <strong>{accountLabel}</strong> gelöscht, da es von keiner weiteren
        Verfolgung genutzt wird. Bereits abgerufene Kontobewegungen werden dabei
        mit entfernt.
        <br />
        <br />
        Wirksam wird beides mit dem Speichern des Vorgangs.
      </>
    );

    const confirmed = await showConfirmation({
      title: 'Informationsverfolgung entfernen',
      message,
      confirmButtonText: 'Entfernen',
      abortButtonText: 'Abbrechen',
    });
    if (!confirmed) return;

    // Eintrag aus dem Vorgangs-Array nehmen. Beim Speichern raeumt
    // ``processmanager.resolveArray`` ihn auf (Soft-Delete ueber
    // ``Deleted_at``) und loescht anschliessend das dann unreferenzierte
    // Notaranderkonto. Vorgang verwerfen = nichts ist passiert.
    setProcessManagerArray((prev: IProcessManager[]) =>
      prev.filter(p => p.idProcessManager !== pm.idProcessManager)
    );
    showAlert({
      text: isNew
        ? 'Die Verfolgung wurde verworfen.'
        : willAlsoDeleteAccount
          ? 'Die Verfolgung wurde entfernt. Sie wird gemeinsam mit dem Notaranderkonto beim Speichern des Vorgangs gelöscht.'
          : 'Die Verfolgung wurde entfernt. Das Entfernen wird mit dem Speichern des Vorgangs wirksam.',
      severity: 'info',
    });
  };

  const automations = pm.Automations ?? [];

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            size='small'
            onClick={() => setExpanded(v => !v)}
            title={
              expanded ? 'Automationen zuklappen' : 'Automationen aufklappen'
            }
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell>
          {accountState === 'unlinked' && (
            <Tooltip title='Dieser Verfolgung ist kein Notaranderkonto zugeordnet. Bitte entfernen und neu anlegen.'>
              <Chip size='small' color='warning' label='⚠ Konto fehlt' />
            </Tooltip>
          )}
          {accountState === 'missing' && (
            <Tooltip
              title={`Das zugeordnete Notaranderkonto (#${idHbci}) ist nicht mehr vorhanden. Die Verfolgung hat damit keine Wirkung und sollte entfernt werden.`}
            >
              <Chip
                size='small'
                color='warning'
                label='⚠ Konto nicht gefunden'
              />
            </Tooltip>
          )}
          {accountState !== 'unlinked' && accountState !== 'missing' && (
            <Typography variant='body2' fontFamily='monospace'>
              {account?.IBAN ?? `#${idHbci}`}
            </Typography>
          )}
        </TableCell>
        <TableCell>{account?.BankName || account?.BIC || '–'}</TableCell>
        <TableCell align='right'>{formatBalance(balance)}</TableCell>
        <TableCell align='right'>
          <Stack
            direction='row'
            spacing={0.5}
            alignItems='center'
            justifyContent='flex-end'
          >
            <Typography variant='body2'>
              {target !== null ? formatBalance(target) : '–'}
            </Typography>
            {targetLocked ? (
              <Tooltip title='Zielsumme bereits erreicht — nicht mehr änderbar'>
                <LockIcon fontSize='inherit' color='disabled' />
              </Tooltip>
            ) : (
              <Tooltip title='Zielsumme bearbeiten'>
                <IconButton
                  size='small'
                  onClick={() => {
                    setEditTargetValue(
                      target !== null ? String(target).replace('.', ',') : ''
                    );
                    setEditTargetOpen(true);
                  }}
                >
                  <EditIcon fontSize='inherit' />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction='row' spacing={0.5} alignItems='center'>
            <Chip
              size='small'
              color={status.color}
              variant={status.variant}
              label={status.label}
            />
          </Stack>
        </TableCell>
        <TableCell align='right'>
          <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
            <Tooltip title='Saldo manuell per HBCI abrufen'>
              <span>
                <IconButton
                  size='small'
                  onClick={handleRefresh}
                  disabled={refreshing || isNew || accountActionsDisabled}
                >
                  {refreshing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshIcon fontSize='small' />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Kontobewegungen einsehen'>
              <span>
                <IconButton
                  size='small'
                  onClick={() => setShowTransactions(true)}
                  disabled={isNew || accountActionsDisabled}
                >
                  <Receipt fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Sichtbarkeit im Mandantenportal (Mandant/Rollen) bearbeiten'>
              <span>
                <IconButton
                  size='small'
                  onClick={() => setVisibilityOpen(true)}
                  disabled={isNew || !account}
                >
                  <GroupIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Verfolgung duplizieren – legt eine neue Verfolgung mit denselben Einstellungen (Konto, Zielsumme, Auslöser, Automationen) für diesen Vorgang an.'>
              <span>
                <IconButton
                  size='small'
                  onClick={handleCopy}
                  disabled={accountActionsDisabled}
                >
                  <ContentCopy fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            {/* Entfernen nur erlaubt, solange die Zielsumme-Automation
                NICHT bereits gefeuert hat. Sobald ``TargetReachedAt`` im
                Backend gesetzt ist, ist der Eintrag ein historischer
                Beleg dafuer, dass die Bedingung erreicht wurde — der
                Notar soll diesen Stand nicht versehentlich wegklicken
                koennen. (Wer ihn wirklich aufraeumen will, kann die
                Verfolgung im Backend per SQL stornieren.) */}
            {!targetLocked && (
              <Tooltip title='Verfolgung entfernen'>
                <IconButton size='small' color='error' onClick={handleRemove}>
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {targetLocked && (
              <Tooltip title='Zielsumme bereits erreicht — Eintrag bleibt als Historiendokument erhalten'>
                <span>
                  <IconButton size='small' disabled>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Collapse-Row für Automationen — zwei Buckets, jeder mit eigener
          PMAutomationsTable. Automationen werden im Backend nur vom
          passenden Event (Saldo-Änderung bzw. Zielsumme erreicht)
          ausgelöst. */}
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
              <Stack
                direction='row'
                spacing={2}
                alignItems='flex-start'
                justifyContent='space-between'
                sx={{ mb: 0.5 }}
              >
                <Box>
                  <Typography variant='subtitle2'>
                    Bei Saldo-Änderung
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1 }}
                  >
                    {balanceChangeMode === 'once' && (
                      <>
                        Wird nur einmal ausgelöst – wenn die Bank das erste Mal
                        einen anderen Saldo als zuvor meldet.
                      </>
                    )}
                    {balanceChangeMode === 'until-target' && (
                      <>
                        Wird bei jeder Saldo-Änderung ausgelöst, bis die
                        Zielsumme erreicht ist. Sobald die Zielsumme erreicht
                        ist, werden keine Änderungs-Benachrichtigungen mehr
                        versandt.
                      </>
                    )}
                  </Typography>
                </Box>
                <FormControl size='small' sx={{ minWidth: 320 }}>
                  <InputLabel id={`bcmode-${pm.idProcessManager}`}>
                    Auslöser-Modus
                  </InputLabel>
                  <Select<BalanceChangeMode>
                    labelId={`bcmode-${pm.idProcessManager}`}
                    label='Auslöser-Modus'
                    value={balanceChangeMode}
                    onChange={e => {
                      const next = e.target.value as BalanceChangeMode;
                      // Beim Wechsel des Modus den ``FiredAt``-Stempel
                      // zuruecksetzen — sonst koennte ein neuer Modus
                      // direkt als „bereits verbraucht" wirken.
                      updatePmData({
                        BalanceChangeMode: next,
                        BalanceChangeFiredAt: undefined,
                      });
                    }}
                    disabled={pm.idProcessManagerState !== 1}
                  >
                    {(['until-target', 'once'] as BalanceChangeMode[]).map(
                      m => (
                        <MenuItem
                          key={m}
                          value={m}
                          disabled={m === 'until-target' && target === null}
                        >
                          {BALANCE_CHANGE_MODE_LABELS[m]}
                          {m === 'until-target' && target === null
                            ? ' (Zielsumme fehlt)'
                            : ''}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Stack>
              {balanceChangeOnceConsumed && (
                <Alert severity='info' sx={{ mb: 1 }}>
                  Einmalige Auslösung bereits am{' '}
                  <strong>
                    {data.BalanceChangeFiredAt
                      ? new Date(data.BalanceChangeFiredAt).toLocaleString(
                          'de-DE'
                        )
                      : '–'}
                  </strong>{' '}
                  geschehen. Weitere Saldoänderungen lösen nichts mehr aus.
                  Setzen Sie den Modus zurück auf „jede Änderung" oder „bis
                  Zielsumme", um den Trigger erneut scharf zu schalten.
                </Alert>
              )}
              {/* Lock-Bedingungen:
                  - Saldo-Bucket  : eingefroren, wenn die Zielsumme bereits
                                    erreicht wurde (``targetLocked``). Der
                                    Tracker hat dann sein Ende-Kriterium
                                    erfuellt und gilt als „abgeschlossen".
                  - Target-Bucket : zusaetzlich eingefroren, wenn gar keine
                                    Zielsumme gesetzt ist (Automation
                                    koennte ohnehin nicht feuern). */}
              <PMAutomationsTable
                processManager={pm}
                automations={automations}
                setAutomations={updateAutomations}
                legalClients={legalClients}
                legalPhases={legalPhases}
                legalReqStates={legalReqStates}
                triggerFilter={TRIGGER_BALANCE}
                defaultTrigger={TRIGGER_BALANCE}
                readOnly={targetLocked}
              />

              <Box sx={{ mt: 3 }}>
                <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                  Bei Zielsumme erreicht
                  {target !== null && (
                    <Chip
                      size='small'
                      variant='outlined'
                      label={formatBalance(target)}
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 1 }}
                >
                  {target === null ? (
                    <em>
                      Keine Zielsumme hinterlegt – bitte hinterlegen Sie eine
                      Zielsumme.
                    </em>
                  ) : targetLocked ? (
                    <em>
                      Zielsumme bereits erreicht — die hinterlegten Automationen
                      sind eingefroren und werden als Historiendokument
                      erhalten.
                    </em>
                  ) : (
                    <>
                      Wird einmalig ausgelöst, sobald der Saldo erstmals die
                      Zielsumme von <strong>{formatBalance(target)}</strong>{' '}
                      erreicht oder überschreitet.
                    </>
                  )}
                </Typography>
                <PMAutomationsTable
                  processManager={pm}
                  automations={automations}
                  setAutomations={updateAutomations}
                  legalClients={legalClients}
                  legalPhases={legalPhases}
                  legalReqStates={legalReqStates}
                  triggerFilter={TRIGGER_TARGET}
                  defaultTrigger={TRIGGER_TARGET}
                  readOnly={target === null || targetLocked}
                />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {showTransactions && idHbci !== null && (
        <HbciTransactionsDialog
          open={showTransactions}
          idHbciAccount={idHbci}
          iban={account?.IBAN}
          bankName={account?.BankName}
          onClose={() => setShowTransactions(false)}
          onTanChallenge={c => setTanChallenge(c.challenge)}
        />
      )}

      <HbciTanChallengeDialog
        open={tanChallenge !== null}
        idLegalTransaction={idLegalTransaction ?? 0}
        idHbciAccount={idHbci ?? 0}
        challenge={tanChallenge}
        onResolved={() => setTanChallenge(null)}
        onError={msg => showAlert({ text: msg, severity: 'error' })}
      />

      {account && (
        <HbciAccountVisibilityDialog
          open={visibilityOpen}
          idLegalTransaction={idLegalTransaction ?? 0}
          account={account}
          participants={legalClients}
          roles={roles}
          onClose={() => setVisibilityOpen(false)}
        />
      )}

      <Dialog
        open={editTargetOpen}
        onClose={() => setEditTargetOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Zielsumme bearbeiten</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Die Zielsumme dient als Auslöser für Automationen im Bucket „Bei
              Zielsumme erreicht". Leeren Sie das Feld, um den Trigger zu
              deaktivieren.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label='Zielsumme (EUR)'
              value={editTargetValue}
              onChange={e => setEditTargetValue(e.target.value)}
              placeholder='z. B. 50000,00'
              inputProps={{ inputMode: 'decimal' }}
            />
            {balance !== null && (
              <Alert severity='info'>
                Aktueller Saldo: <strong>{formatBalance(balance)}</strong>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTargetOpen(false)}>Abbrechen</Button>
          <Button variant='contained' onClick={handleSaveTarget}>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
